import React, { useState, useEffect, useRef } from 'react';
import { formatTextContent } from '../utils/textformatting';
import '../utils/textformatting.css';
import { API_BASE, authFetch } from '../services/api.js';

const LearningPageContentSection = ({ 
  currentMicroChunk, 
  currentLanguage, 
  type // 'explanation' or 'examples'
}) => {
  const [content, setContent] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [expandedSentences, setExpandedSentences] = useState(2);
  const [loading, setLoading] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);

  // Configuration based on type
  const config = {
    explanation: {
      icon: '💡',
      title: 'Explanation',
      color: '#4c51bf',
      apiEndpoint: 'explanations',
      aiEndpoint: 'ai-explanation',
      emptyMessage: 'No explanations available. Click "🤖 AI Explain" to generate one.',
      aiButtonText: '🤖 AI Explain',
      aiGeneratingText: '🤖 Generating...'
    },
    examples: {
      icon: '📝',
      title: 'Examples',
      color: '#38a169',
      apiEndpoint: 'examples',
      aiEndpoint: 'ai-examples',
      emptyMessage: 'No examples available. Click "🤖 Generate Examples" to create some.',
      aiButtonText: '🤖 Generate Examples',
      aiGeneratingText: '🤖 Generating...'
    }
  };

  const currentConfig = config[type];
  // Load content when component mounts or language changes
  
  useEffect(() => {
    loadContent();
  }, [currentMicroChunk, currentLanguage, type]);

  // Reset expanded sentences when switching content
  useEffect(() => {
    setExpandedSentences(2);
  }, [currentIndex]);

  

  // Add this at the top of the component (after useState declarations)
  const isLoadingRef = useRef(false);
  
  const loadContent = async () => {
    
    if (!currentMicroChunk?.micro_chunk_id) return;

    // Prevent duplicate calls
    if (isLoadingRef.current) {
      //console.log("loadContent blocked - already running");
      return;
    }
      
    isLoadingRef.current = true;
    setLoading(true);
    try {
      // Use the new database-only endpoint
      const userData = JSON.parse(localStorage.getItem('userData'));
      const studentId = userData?.student?.student_id;
      const response = await authFetch(`${API_BASE}/teach/get-db-explanations?micro_chunk_id=${currentMicroChunk.micro_chunk_id}&meta_tag=${type}&language=${currentLanguage}&student_id=${studentId}`);
      
      if (response.ok) {
        const data = await response.json();
        if (data && data.length > 0) {
          setContent(data);
          setCurrentIndex(0);
        } else {
          setContent([]); // Empty array if no database content
          await generateAIContent();
        }
      } else {
        setContent([]);
        await generateAIContent();
      }
    } catch (error) {
      //console.error(`Error loading ${type}:`, error);
      setContent([]);
      await generateAIContent();
    }
    setLoading(false);
    isLoadingRef.current = false; // Reset the flag
  };
  
  const generateAIContent = async () => {
    //console.log("AI generation triggered", Date.now());
    if (aiGenerating) return; // Prevent multiple calls
    
    setAiGenerating(true);
    try {
      // temporary generation only and not saved
        
      const userData = JSON.parse(localStorage.getItem('userData'));
      const studentId = userData?.student?.student_id;
        const response = await authFetch(`${API_BASE}/teach/get-explanation?student_id=${studentId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          micro_chunk_id: currentMicroChunk.micro_chunk_id,
          meta_tag: type === 'explanation' ? 'explanation' : 'examples',
          language: currentLanguage
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log("Full AI response:", result.content); // Add this
        console.log("Response length:", result.content.length); // Add this
        const aiContent = {
          content: result.content,
          source: 'ai',
          id: Date.now()
        };
        //setContent(prev => [...prev, aiContent]); // This ADDS to existing content
        
        // Update content and calculate the new index manually
        setContent(prev => {
          const newContent = [...prev, aiContent];
          // Set index to the last item (which is the newly added one)
          setTimeout(() => setCurrentIndex(newContent.length - 1), 0);
          return newContent;
        });
          
        
      } else {
        console.error(`Failed to generate AI ${type}:`, response.status);
      }
    } catch (error) {
      //console.error(`Error generating AI ${type}:`, error);
    }
    setAiGenerating(false);
  };

  const splitIntoSentences = (text) => {
    if (!text) return [];
    return text.split(/[.!?]+/).filter(sentence => sentence.trim().length > 0);
  };

  const getCurrentContent = () => {
    return content[currentIndex] || null;
  };

  const getCurrentText = () => {
    const currentItem = getCurrentContent();
    if (!currentItem) return '';
    
    const text = currentItem.content || currentItem.explanation_text || currentItem.example_text || '';
    const sentences = splitIntoSentences(text);
    const visibleSentences = sentences.slice(0, expandedSentences);
    const visibleText = visibleSentences.join('. ') + (visibleSentences.length > 0 ? '.' : '');
    // Use formatTextContent instead of returning plain text
    return formatTextContent(visibleText);
  };

  const hasMoreSentences = () => {
    const currentItem = getCurrentContent();
    if (!currentItem) return false;
    
    const text = currentItem.content || currentItem.explanation_text || currentItem.example_text || '';
    const sentences = splitIntoSentences(text);
    return expandedSentences < sentences.length;
  };

  const showMoreSentences = () => {
    setExpandedSentences(prev => prev + 2);
  };

  const goToPrevious = () => {
    setCurrentIndex(prev => prev > 0 ? prev - 1 : prev); // Stop at 0
  };

  const goToNext = () => {
    setCurrentIndex(prev => prev < content.length - 1 ? prev + 1 : prev); // Stop at last
  };

  if (loading) {
    return (
      <div style={{
        background: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        padding: '20px',
        textAlign: 'center'
      }}>
        <div>Loading {type}...</div>
      </div>
    );
  }

  return (
    <div style={{
      background: '#f8fafc',
      border: '1px solid #e2e8f0',
      borderRadius: '12px',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '15px 20px',
        backgroundColor: currentConfig.color,
        color: 'white'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <span style={{fontSize: '16px', fontWeight: '600'}}>
            {currentConfig.icon} {currentConfig.title}
          </span>
          
          {content.length > 0 && (
            <span style={{fontSize: '14px', opacity: 0.9}}>
              {currentIndex + 1} of {content.length}
            </span>
          )}
        </div>

        <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
          {/* Navigation arrows */}
          {content.length > 1 && (
            <>
              <button
                onClick={goToPrevious}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: '30px',
                  height: '30px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ←
              </button>
              
              <button
                onClick={goToNext}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: '30px',
                  height: '30px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                →
              </button>
            </>
          )}

          {/* AI Generate button */}
          <button
            onClick={generateAIContent}
            disabled={aiGenerating}
            style={{
              background: aiGenerating ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              padding: '6px 12px',
              borderRadius: '4px',
              cursor: aiGenerating ? 'not-allowed' : 'pointer',
              fontSize: '12px',
              fontWeight: '600'
            }}
          >
            {aiGenerating ? currentConfig.aiGeneratingText : currentConfig.aiButtonText}
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '20px' }}>
        {content.length === 0 ? (
          <div style={{
            textAlign: 'center',
            color: '#6b7280',
            fontStyle: 'italic',
            marginBottom: '15px'
          }}>
            {currentConfig.emptyMessage}
          </div>
        ) : (
          <div>
            <div style={{
              fontSize: '15px',
              lineHeight: '1.6',
              color: '#2d3748',
              marginBottom: '15px'
            }}className="explanation-content">
              {getCurrentText()} {/* This now returns formatted JSX */}
            </div>

            {/* Show More button */}
            {hasMoreSentences() && (
              <button
                onClick={showMoreSentences}
                style={{
                  background: 'none',
                  border: 'none',
                  color: currentConfig.color,
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  textDecoration: 'underline'
                }}
              >
                more...
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LearningPageContentSection;